// components/DayCard.tsx
import { Box, Typography, Paper, Chip, Divider, Tooltip } from "@mui/material";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import ManageTask from "../ManageTask";
import timezone from "dayjs/plugin/timezone"; // required for .local()
import utc from "dayjs/plugin/utc";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Toggle from "../../../../components/common/toggle/Toggle";
import { updateWorkDiaryBookingStatusAction } from "../../../../store/reducers/workDiarySlice";

dayjs.extend(utc);
type Task = {
  start_date_time: string;
  customer_name: string;
  route: string;
  color: string;
  mileage?: string;
  meter?: string;
};

interface DayCardProps {
  date: string;
  tasks: Task[];
  confirmedTotal: number;
  provisionalTotal: number;
  confirmedTaskCount: number;
  totalMileage: string;
  isBooked?: boolean;
}

const DayCard = ({
  date,
  tasks,
  confirmedTotal,
  provisionalTotal,
  confirmedTaskCount,
  totalMileage,
  isBooked,
}: DayCardProps) => {
  dayjs.extend(timezone);
  const [open, setOpen] = useState(false);
  const { userProfile } = useSelector((state: any) => state?.user);
  const dispatch = useDispatch();
  const today = dayjs();
  const [selectedTask, setSelectedTask] = useState("");
  const [isProfessionalSection, setIsProfessionalSection] = useState(
    userProfile?.features?.includes("Professional Work Diary") || false, // Safe fallback
  );

  useEffect(() => {
    setIsProfessionalSection(
      userProfile?.features?.includes("Professional Work Diary") || false,
    ); // Sync profile changes
  }, [userProfile]);

  const handleOpen = (event?: any) => {
    setOpen(true);
  };
  const handleSelectEvent = (event: any) => {
    setSelectedTask(event?.task_id || event?.job_id);
    handleOpen(event);
  };
  const handleClose = () => {
    setOpen(false);
    setSelectedTask("");
  };

  const totalVolume = tasks
    .reduce((sum: number, task: any) => {
      if (Array.isArray(task.collection_addresses)) {
        const collectionVolume = task.collection_addresses.reduce(
          (innerSum: number, addr: any) =>
            innerSum + (Number(addr.volume) || 0),
          0,
        );
        return sum + collectionVolume;
      }
      return sum;
    }, 0)
    .toFixed(2);

  const totalDistance = tasks
    .reduce((sum: number, task: any) => {
      const collectionDistance = Array.isArray(task.collection_addresses)
        ? task.collection_addresses.reduce(
            (innerSum: number, addr: any) =>
              innerSum + (Number(addr.distance) || 0),
            0,
          )
        : 0;

      const deliveryDistance = Array.isArray(task.delivery_addresses)
        ? task.delivery_addresses.reduce(
            (innerSum: number, addr: any) =>
              innerSum + (Number(addr.distance) || 0),
            0,
          )
        : 0;

      const returningDistance = Number(task.returning_distance) || 0;

      return sum + collectionDistance + deliveryDistance + returningDistance;
    }, 0)
    .toFixed(2);

  const navigate = useNavigate();

  const handleSelectedJob = (task: any) => {
    sessionStorage.setItem("selectedJobLeadId", JSON.stringify(task?.lead_id));
    navigate("/dashboard");
  };

  const JOB_TITLE_CONFIG: Record<
    string,
    { label?: string; bg: string; color: string }
  > = {
    packing: { label: "pack", bg: "#FFE082", color: "#000" },
    moving: { label: "move", bg: "#FFEB3B", color: "#000" },
    clearance: { label: "clear", bg: "#FFAB91", color: "#000" },
    "in store": { label: "inStore", bg: "#CE93D8", color: "#000" },
    "out of store": { label: "outStore", bg: "#B0BEC5", color: "#000" },
    cleaning: { label: "clean", bg: "#A5D6A7", color: "#000" },
    "pack-move": { label: "pack-move", bg: "#0fa293", color: "#000" },
    /* General tasks */
    "task general": { label: "task", bg: "#800080", color: "#fff" },
    "box delivery": { label: "box-deliver", bg: "#800080", color: "#fff" },
    mot: { label: "mot", bg: "#800080", color: "#fff" },
    maintenance: { label: "maint", bg: "#800080", color: "#fff" },
    repair: { label: "repair", bg: "#800080", color: "#fff" },
    deliver: { label: "deliver", bg: "#800080", color: "#fff" },
    collect: { label: "collect", bg: "#800080", color: "#fff" },
  };

  const getJobConfig = (title: string = "") => {
    const normalized = title.toLowerCase().trim();
    const config = JOB_TITLE_CONFIG[normalized];

    return {
      label: config?.label ?? title,
      bg: config?.bg ?? "#009688",
      color: config?.color ?? "#fff",
    };
  };

  /* ---------------- STATUS TILE ---------------- */

  const StatusTile = ({ label, date, tooltip }: any) => (
    <Tooltip title={tooltip} arrow>
      <Box
        sx={{
          width: 19,
          height: 19,
          bgcolor:
            label === "J"
              ? "#990000"
              : label === "T"
                ? "#FF00FF"
                : label === "P"
                  ? "#FFFF00"
                  : label === "S" ||
                      label === "R" ||
                      label === "I" ||
                      label === "C"
                    ? "#46BDC6"
                    : "transparent",
          color: label === "J" || label === "T" ? "#fff" : "#000",
          borderRadius: "2px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {/* <Typography sx={{ fontSize: 8 }}>
          {dayjs(date).format("DD MMM").toUpperCase()}
        </Typography> */}
        <Typography
          sx={{
            fontWeight: 500,
            fontSize: 16,
            lineHeight: "19px",
            // color: "#fff",
            width: 11,
            height: 19,
          }}
        >
          {label}
        </Typography>
      </Box>
    </Tooltip>
  );

  const STAGE_COLOR_CONFIG: Record<string, { bg: string; color: string }> = {
    SURVEY: { bg: "#00ff00", color: "#000" },
    SURVEY_COMPLETED: { bg: "#00ffff", color: "#000" },
    QUOTE: { bg: "#ff0000", color: "#000" },
    PROVISIONAL: { bg: "#FFFF00", color: "#000" },
    CONFIRMED: { bg: "#7C3014", color: "#FFF" },
    JOB: { bg: "#a52a2a", color: "#FFF" },
    COMPLETED: { bg: "#FFF7D2", color: "#000" },
    ARCHIVED: { bg: "#666666", color: "#FFF" },
    AWAITING: { bg: "#0066ff", color: "#FFF" },
    LOST: { bg: "#f6b26b", color: "#000" },
    DISPUTE: { bg: "#000000", color: "#FFF" },
    SCHEDULED: { bg: "#a52a2a", color: "#FFF" },
    "task general": { bg: "#800080", color: "#fff" },
    TASK: { bg: "#800080", color: "#fff" },
  };

  const getStageColor = (stage: string, surveyStatus?: string) => {
    if (stage === "SURVEY" && surveyStatus === "COMPLETED") {
      return STAGE_COLOR_CONFIG["SURVEY_COMPLETED"];
    }

    return (
      STAGE_COLOR_CONFIG[stage] || {
        bg: "#009688",
        color: "#fff",
      }
    );
  };

  // NEW: statuses that should override the "JOB" look when lead_status is "JOB"
  const JOB_STATUS_OVERRIDE = ["PROVISIONAL", "AWAITING"];

  // NEW: resolves which stage to actually use for color/label
  // - lead_status "JOB" + status "SCHEDULED" (or no override match) -> keep JOB look (unchanged)
  // - lead_status "JOB" + status "PROVISIONAL"/"AWAITING" -> use that status's look instead
  const getEffectiveStage = (task: any) => {
    if (
      // task?.lead_status === "JOB" &&
      JOB_STATUS_OVERRIDE.includes(task?.status)
    ) {
      return task.status;
    }
    return task?.lead_status;
  };

  const formatLabel = (stage: string) =>
    stage ? stage.charAt(0) + stage.slice(1).toLowerCase() : stage;

  const QuoteExpireTile = ({ data }: any) => (
    <Tooltip title={data?.tooltip} arrow>
      <Box
        sx={{
          width: 36,
          height: 20,
          bgcolor: data?.isExpired ? "#e60a18" : "#990000",
          color: "#fff",
          borderRadius: 0.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 8,
          fontWeight: 700,
          lineHeight: 1,
        }}
      >
        {dayjs(data?.date).format("DD MMM").toUpperCase()}
      </Box>
    </Tooltip>
  );

  const handleChangeSwitch = (event: any) => {
    const newStatus = event.target.checked;
    dispatch(
      updateWorkDiaryBookingStatusAction({ date: date, isBooked: newStatus }),
    );
  };

  return (
    <>
      {open && (
        <ManageTask
          selectedTask={selectedTask}
          open={open}
          onHide={handleClose}
          title="SCHEDULE A TASK"
        />
      )}
      <Paper
        sx={{
          width: 327,
          maxWidth: "100%",
          mx: 0.5,
          p: 0,
          flexShrink: 0,
          height: "auto",
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <Box bgcolor="#009688" color="white" px={1} py={0.5} borderRadius={1}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            {/* <Typography variant="subtitle2">{date}</Typography> */}
            <Typography variant="subtitle2">
              {dayjs(date).format("ddd, MMM D, YYYY")}
              {/* {date} */}
            </Typography>
            {/* <Chip
            label="Task"
            size="small"
            sx={{
              bgcolor: "white",
              color: "#666",
              fontSize: 10,
              fontWeight: 600,
              borderRadius: "5px",
            }}
          /> */}
            <Toggle
              name="Professional View"
              checked={isBooked}
              onChange={handleChangeSwitch}
              sx={{
                "& .MuiSwitch-switchBase.Mui-checked": {
                  color: "#1B7B67",
                },
                "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                  backgroundColor: "#1B7B67",
                  opacity: 1,
                },
                "& .MuiSwitch-track": {
                  backgroundColor: "#ccc",
                  opacity: 1,
                },
                "&.Mui-disabled .MuiSwitch-thumb": {
                  color: "#999",
                },
                "&.Mui-disabled .MuiSwitch-track": {
                  backgroundColor: "#ddd",
                  opacity: 1,
                },
              }}
            />
          </Box>
        </Box>
        {/* Tasks */}
        <Box
          sx={{
            flexGrow: 1,
            overflowY: "auto",
            px: 1,
            py: 1,
          }}
        >
          {tasks.map((task: any, idx: number) => {
            const jobConfig = getJobConfig(task?.job_title);

            // NEW: resolve effective stage (status overrides JOB when applicable)
            const effectiveStage = getEffectiveStage(task);
            const isStatusOverride = effectiveStage !== task?.lead_status;

            const stageConfig = getStageColor(
              effectiveStage,
              task?.stageSurveyStatus?.status,
            );
            const isTaskStyle = !task?.lead_status;
            const bgColor = isTaskStyle ? "#800080" : stageConfig.bg;
            // task?.status === "PROVISIONAL" && task?.is_confirmation_pending
            //   ? "#FFF9C4"
            //   : task?.is_confirmation_pending
            //     ? "#0fa293"
            //     : jobConfig.bg;

            const textColor = isTaskStyle ? "#fff" : stageConfig.color;
            // task?.status === "PROVISIONAL" && task?.is_confirmation_pending
            //   ? "#000"
            //   : task?.is_confirmation_pending
            //     ? "#000"
            //     : jobConfig.color;

            // NEW: label shows the overriding status (e.g. "Provisional") when applicable,
            // otherwise falls back to the usual job title label (unchanged behavior)
            const displayLabel = isStatusOverride
              ? jobConfig?.label
              : jobConfig?.label;

            const collectionTown =
              task?.collection_addresses?.[0]?.postcode ??
              task?.collection_addresses?.[0]?.town ??
              "";
            const deliveryTown =
              task?.delivery_addresses?.[task?.delivery_addresses?.length - 1]
                ?.postcode ??
              task?.delivery_addresses?.[task?.delivery_addresses?.length - 1]
                ?.town ??
              "";

            const addressText = task?.job_id
              ? deliveryTown
                ? `${collectionTown} → ${deliveryTown}`
                : collectionTown
              : task?.location || "";

            const collection = task?.collection_addresses?.[0];
            const delivery =
              task?.delivery_addresses?.[task?.delivery_addresses?.length - 1];

            const addressToolTip = task?.job_id ? (
              <>
                {/* Collection Address */}
                {(collection?.street ||
                  collection?.town ||
                  collection?.country ||
                  collection?.postcode) && (
                  <>
                    {collection?.street} {collection?.town}{" "}
                    {collection?.country} {collection?.postcode}
                  </>
                )}

                {/* Collection Status */}
                {collection?.purchase_status && (
                  <>
                    <br />
                    {collection.purchase_status}
                  </>
                )}

                {/* Show "to" only if delivery exists */}
                {(delivery?.street ||
                  delivery?.town ||
                  delivery?.country ||
                  delivery?.postcode) && (
                  <>
                    <br />
                    <b>to</b>
                    <br />
                    {delivery?.street} {delivery?.town} {delivery?.country}{" "}
                    {delivery?.postcode}
                  </>
                )}

                {/* Delivery Status */}
                {delivery?.purchase_status && (
                  <>
                    <br />
                    {delivery.purchase_status}
                  </>
                )}
              </>
            ) : (
              (task?.address ?? "")
            );

            // task volume

            const taskVolume = Array.isArray(task.collection_addresses)
              ? task.collection_addresses.reduce(
                  (sum: number, addr: any) => sum + (Number(addr.volume) || 0),
                  0,
                )
              : 0;

            //  task distance
            const taskDistance =
              (Array.isArray(task.collection_addresses)
                ? task.collection_addresses.reduce(
                    (sum: number, addr: any) =>
                      sum + (Number(addr.distance) || 0),
                    0,
                  )
                : 0) +
              (Array.isArray(task.delivery_addresses)
                ? task.delivery_addresses.reduce(
                    (sum: number, addr: any) =>
                      sum + (Number(addr.distance) || 0),
                    0,
                  )
                : 0) +
              (Number(task.returning_distance) || 0);

            /* ---------- TILE DATA ---------- */
            const MOVE_TYPE_TILE: any = {
              Residential: {
                label: "R",
                tooltip: "Small load Type: Residential",
              },
              International: {
                label: "I",
                tooltip: "Small load Type: International",
              },
              Commercial: {
                label: "C",
                tooltip: "Small load Type: Commercial",
              },
              Domestic: { label: "S", tooltip: "Small load Type: Domestic" },
            };

            const moveTile = MOVE_TYPE_TILE[task?.type_of_move];

            const statusTiles = [
              task?.job_sheet_print_date && {
                label: "J",
                date: task?.job_sheet_print_date,
                tooltip: `Job Sheet Printed ${dayjs(task?.job_sheet_print_date).format("DD MMM").toUpperCase()}`,
              },
              task?.is_terms_accepted && {
                label: "T",
                date: task?.start_date_time,
                tooltip: `Terms & conditions have been accepted ${dayjs(task?.terms_accepted_date).format("DD MMM").toUpperCase()}`,
              },

              task?.packing_sheet_print_date && {
                label: "P",
                date: task?.packing_sheet_print_date,
                tooltip: `Packing Sheet Printed ${dayjs(task?.packing_sheet_print_date).format("DD MMM").toUpperCase()}`,
              },
              moveTile && {
                ...moveTile,
                date: task?.start_date_time,
              },
            ];

            const quoteExpireTileData = task?.quote_expires_on
              ? dayjs(task?.quote_expires_on).isAfter(dayjs())
                ? {
                    date: task?.quote_expires_on,
                    tooltip: `Quote expires on ${dayjs(task?.quote_expires_on).format("DD MMM YY")}`,
                    isExpired: false,
                  }
                : {
                    date: task?.quote_expires_on,
                    tooltip: `Quote expired on ${dayjs(task?.quote_expires_on).format("DD MMM YY")}`,
                    isExpired: true,
                  }
              : null;

            return (
              <Paper
                key={idx}
                sx={{
                  width: "100%",
                  maxWidth: "100%",
                  background: idx % 2 === 0 ? "#ebe9e9" : "#F5F5F5",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  padding: "10px",
                  gap: "4px",
                  boxShadow: "0px 1px 4px rgba(0,0,0,0.04)",
                  borderRadius: "8px",
                  marginBottom: 1,
                  boxSizing: "border-box",
                  overflow: "hidden",
                }}
              >
                {/* Top Row: Time and Date */}
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "space-between",
                    alignItems: "center",
                    width: 305,
                    height: 19,
                    padding: 0,
                    gap: "8px",
                  }}
                >
                  <>
                    <Typography
                      sx={{
                        fontFamily: "Inter",
                        fontWeight: 500,
                        fontSize: 16,
                        lineHeight: "19px",
                        color: "#0A0A0A",
                        width: 44,
                        height: 19,
                        margin: 0,
                      }}
                    >
                      {task?.tag?.toUpperCase() === "END"
                        ? dayjs.utc(task.end_date_time).format("HH:mm")
                        : task?.start_date_time
                          ? dayjs.utc(task.start_date_time).format("HH:mm")
                          : "--:--"}
                    </Typography>
                    {task?.tag && (
                      <Typography
                        sx={{
                          fontFamily: "Inter",
                          fontWeight: 600,
                          fontSize: 13,
                          lineHeight: "19px",
                          color: "#fff",
                          backgroundColor: "#888787",
                          borderRadius: "4px",
                          width: 51,
                          height: 19,
                          margin: 0,
                          textAlign: "center",
                        }}
                      >
                        {task?.tag?.toUpperCase()}
                      </Typography>
                    )}
                  </>
                  <Typography
                    sx={{
                      fontFamily: "Inter",
                      fontWeight: 600,
                      fontSize: 16,
                      lineHeight: "19px",
                      color: "#E60A18",
                      width: 51,
                      height: 19,
                      margin: 0,
                      textAlign: "right",
                    }}
                  >
                    {quoteExpireTileData && (
                      <QuoteExpireTile data={quoteExpireTileData} />
                    )}
                  </Typography>
                </Box>
                {/* Divider */}
                <Divider
                  sx={{
                    width: 305,
                    borderColor: idx % 2 === 0 ? "#fbfbfb" : "#E6E6E6",
                    my: 0,
                  }}
                />
                {/* Main Row: Job Type, Status Tiles */}
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "space-between",
                    alignItems: "center",
                    width: 305,
                    height: 21,
                    gap: "8px",
                    padding: 0,
                  }}
                >
                  {/* Job Type */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      width: 160,
                      minWidth: 0,
                      overflow: "hidden",
                      padding: "1px 4px",
                      bgcolor: bgColor,
                      borderRadius: "4px",
                      textTransform: "uppercase",
                      cursor: "pointer",
                      boxSizing: "border-box",
                    }}
                  >
                    {task?.lead_status ? (
                      <Tooltip
                        title={
                          <Box>
                            {displayLabel}
                            <div>
                              {isStatusOverride
                                ? task?.status
                                : task?.lead_status}
                            </div>
                            {task?.quote_total && (
                              <div>
                                £ {Number(task?.quote_total).toFixed(2)}
                              </div>
                            )}
                          </Box>
                        }
                        arrow
                      >
                        <Typography
                          noWrap
                          sx={{
                            flex: 1,
                            minWidth: 0,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            fontWeight: 700,
                            fontSize: 12,
                            color: textColor,
                          }}
                        >
                          {displayLabel}
                        </Typography>
                      </Tooltip>
                    ) : (
                      <Typography
                        noWrap
                        sx={{
                          flex: 1,
                          minWidth: 0,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          fontWeight: 700,
                          fontSize: 12,
                          color: textColor,
                        }}
                      >
                        {displayLabel}
                      </Typography>
                    )}
                  </Box>
                  {/* Status Tiles */}
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                      gap: "6px",
                      width: 94,
                      height: 19,
                    }}
                  >
                    {statusTiles.map((tile, i) => (
                      <StatusTile key={i} {...tile} />
                    ))}
                  </Box>
                </Box>
                {/* Customer Name and Distance */}
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "space-between",
                    alignItems: "center",
                    width: "100%",
                    // height: 21,
                    gap: "8px",
                    padding: 0,
                  }}
                  onClick={() => {
                    task?.task_id
                      ? handleSelectEvent(task)
                      : handleSelectedJob(task);
                  }}
                >
                  <Tooltip title={task.customer_name} arrow>
                    <Typography
                      sx={{
                        fontWeight: 600,
                        fontSize: 15,
                        lineHeight: "19px",
                        color: "#0A0A0A",
                        width: "100%",
                        height: 19,
                        cursor: "pointer",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {task.customer_name}
                    </Typography>
                  </Tooltip>
                  <Typography
                    sx={{
                      fontWeight: 500,
                      fontSize: 14,
                      lineHeight: "19px",
                      color: "#288F60",
                      width: 56,
                      height: 19,
                      textAlign: "right",
                    }}
                  >
                    {taskVolume.toFixed(2)}
                    {task?.volume_unit ?? "m3"}
                  </Typography>
                </Box>
                {/* Route and Volume */}
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "space-between",
                    alignItems: "center",
                    width: "100%",
                    // height: 19,
                    gap: "8px",
                    padding: 0,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                      gap: "8px",
                      width: "100%",
                      height: 17,
                    }}
                  >
                    {task?.job_id ? (
                      <Tooltip title={addressToolTip} arrow>
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "row",
                            alignItems: "center",
                            gap: "8px",
                            width: "100%",
                            height: 17,
                            cursor: "pointer",
                          }}
                        >
                          {/* LEFT ADDRESS */}
                          <Typography
                            sx={{
                              fontWeight: 400,
                              fontSize: 14,
                              lineHeight: "17px",
                              color: "#0A0A0A",
                              maxWidth: 120,
                              whiteSpace: "nowrap",
                              flexShrink: 1,
                            }}
                          >
                            {collectionTown}
                          </Typography>
                          {/* SHOW ONLY IF deliveryTown EXISTS */}
                          {deliveryTown && (
                            <>
                              {/* ARROW */}
                              <Typography
                                sx={{
                                  fontWeight: 400,
                                  fontSize: 14,
                                  lineHeight: "17px",
                                  color: "#8A8A8A",
                                  flexShrink: 0,
                                }}
                              >
                                →
                              </Typography>

                              {/* RIGHT ADDRESS */}
                              <Typography
                                sx={{
                                  fontWeight: 400,
                                  fontSize: 14,
                                  lineHeight: "17px",
                                  color: "#0A0A0A",
                                  maxWidth: 120,
                                  whiteSpace: "nowrap",
                                  flexShrink: 1,
                                }}
                              >
                                {deliveryTown}
                              </Typography>
                            </>
                          )}
                        </Box>
                      </Tooltip>
                    ) : (
                      <Typography
                        sx={{
                          fontSize: 14,
                          color: "#666",
                        }}
                      >
                        {addressText}
                      </Typography>
                    )}
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "flex-start",
                      gap: "4px",
                      width: 60,
                      height: 19,
                    }}
                  >
                    <Typography
                      sx={{
                        fontWeight: 500,
                        fontSize: 14,
                        lineHeight: "19px",
                        color: "#288F60",
                        width: 60,
                        height: 19,
                        textAlign: "right",
                      }}
                    >
                      {Number(taskDistance).toFixed(2)}mi
                    </Typography>
                  </Box>
                </Box>
                {task?.is_confirmation_pending && !task?.is_terms_accepted && (
                  <>
                    {/* Divider */}
                    <Divider
                      sx={{ width: "100%", borderColor: "#E6E6E6", my: 0 }}
                    />
                    {/* T&C Pending */}
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                        width: 305,
                        height: 19,
                        gap: "8px",
                        padding: 0,
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "row",
                          alignItems: "center",
                          gap: "6px",
                          width: 197,
                          height: 19,
                        }}
                      >
                        <Typography
                          sx={{
                            fontFamily: "Inter",
                            fontWeight: 500,
                            fontSize: 14,
                            lineHeight: "19px",
                            color: "#E31ABF",
                            width: 197,
                            height: 19,
                          }}
                        >
                          T&C confirmation pending
                        </Typography>
                      </Box>
                    </Box>
                  </>
                )}
              </Paper>
            );
          })}
        </Box>
        {/* Totals */}
        {isProfessionalSection && (
          <Box
            sx={{
              px: 1.5,
              py: 1,
              backgroundColor: "#0D9488",
              borderTop: "1px solid rgba(255,255,255,0.2)",
              borderRadius: "0 0 6px 6px",
              mt: "auto",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                fontSize: 14,
                color: "#fff",
                mb: 0.5,
              }}
            >
              <Box>
                Confirmation total: £{Number(confirmedTotal || 0).toFixed(2)}
              </Box>

              <Box>Total: {Number(totalVolume || 0).toFixed(2)}m³</Box>
            </Box>

            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                fontSize: 14,
                color: "#fff",
              }}
            >
              <Box>
                Provisional total: £{Number(provisionalTotal || 0).toFixed(2)}
              </Box>

              <Box>Total: {Number(totalMileage || 0).toFixed(2)}mi</Box>
            </Box>
            {/* task  */}
            {/* <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                fontSize: 12,
                color: "#fff",
              }}
            >
              <Box>
                Total Confirmed tasks: {Number(confirmedTaskCount || 0).toFixed(2)}
              </Box>

              <Box>Total: {Number(totalMileage || 0).toFixed(2)}mi</Box>
            </Box> */}
          </Box>
        )}
      </Paper>
    </>
  );
};

export default DayCard;
